import { parseTime } from './clock.js';

/**
 * The board: which events are posted, which one you took, and how long you have
 * left on it.
 *
 * One accepted BEO at a time. That is a design rule, not a technical limit — a
 * banquet server juggling two events is not a harder version of this game, it
 * is a spreadsheet, and the whole point of accepting an event is that from that
 * moment you are committed and the clock is the antagonist.
 *
 * Two moments fire exactly once each, from `update`, whether or not the player
 * is anywhere near the ballroom when they pass: `due` at the setup time, after
 * which the room is late and every minute costs its grade, and `deadline` at
 * the doors, when the guests walk in on whatever is standing. A deadline you
 * can dodge by standing somewhere else is not a deadline.
 *
 * Pure: it is handed a clock and answers questions. It does not grade the room
 * (that is `grading.js`) and it does not move any standing.
 */

export const BEO_STATE = Object.freeze({
  POSTED: 'posted',
  ACCEPTED: 'accepted',
  GRADED: 'graded',
  MISSED: 'missed',
  /** Went ahead without you. Somebody else set it, for better or worse. */
  GHOST: 'ghost',
});

/** A BEO's times, resolved to minutes and carried past midnight if it wraps. */
export function timesOf(beo) {
  const posted = parseTime(beo.postedAt);
  let setupBy = parseTime(beo.setupBy);
  let doors = parseTime(beo.doors);
  // An event that runs past midnight would otherwise have a deadline that
  // reads as earlier than the moment it was posted.
  if (setupBy < posted) setupBy += 24 * 60;
  if (doors < setupBy) doors += 24 * 60;
  return { posted, setupBy, doors };
}

export function createBeoBoard({ catalogue, resolveGhost = null }) {
  /** Finished events, keyed by id, so a day survives a reload. */
  let history = new Map();
  let active = null;
  /**
   * Events posted on top of the generated board, by id.
   *
   * Only the reference event uses this, and only the benchmark script posts it.
   * It lives here rather than in the generator because the generator's whole
   * contract is that a day is a pure function of the seed and the day number.
   */
  let injected = [];
  const listeners = new Set();

  function catalogueFor(day) {
    const generated = catalogue(day);
    return injected.length ? [...injected, ...generated] : generated;
  }

  function emit(event, payload) {
    for (const listener of listeners) listener(event, payload);
  }

  /** The last day `update` saw, so a rollover can take the leftovers with it. */
  let lastDay = null;

  /**
   * Resolves everything posted on a day whose deadline has passed and which is
   * neither yours nor already filed.
   *
   * The load handed to the ghost model is how much of that day's board is going
   * unset, counted before any of it resolves — so five abandoned events are all
   * judged against the same stretched department rather than each one being
   * slightly worse than the last.
   */
  function sweep(day, upTo) {
    if (!resolveGhost) return;
    /*
     * Only what somebody else would have done without you. A card that says
     * `ghosts: false` is work that simply is not done if you do not do it — a
     * lawn nobody struck is still a lawn with chairs on it — so it is neither
     * resolved nor counted in how stretched the department was.
     */
    const posted = catalogueFor(day).filter((beo) => beo.ghosts !== false);
    const stranded = posted.filter(
      (beo) =>
        !history.has(beo.id) && active?.beo.id !== beo.id && upTo >= timesOf(beo).setupBy
    );
    if (!stranded.length) return;

    const unset = posted.filter((beo) => !history.has(beo.id) && active?.beo.id !== beo.id).length;
    const load = posted.length ? unset / posted.length : 0;

    for (const beo of stranded) {
      const result = resolveGhost({ beo, day, load });
      history.set(beo.id, {
        id: beo.id,
        code: beo.code,
        title: beo.title,
        owner: beo.owner,
        day,
        ghost: true,
        onTime: false,
        grade: result?.letter ?? null,
        score: result?.score ?? 0,
        breakdown: [],
      });
      emit('ghost', { beo, result });
    }
  }

  function statusOf(beo, clock) {
    const past = history.get(beo.id);
    if (past) {
      if (past.ghost) return BEO_STATE.GHOST;
      return past.grade === null ? BEO_STATE.MISSED : BEO_STATE.GRADED;
    }
    if (active?.beo.id === beo.id) return BEO_STATE.ACCEPTED;
    return BEO_STATE.POSTED;
  }

  return {
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    get active() {
      return active;
    },
    get history() {
      return [...history.values()];
    },

    /** Puts a hand-written event on today's board. Used by the benchmark. */
    inject(beo) {
      if (!injected.some((entry) => entry.id === beo.id)) injected.push(beo);
    },

    catalogueFor,

    /** Everything on the board today, with enough state to draw a card. */
    board(clock) {
      return catalogueFor(clock.day).map((beo) => {
        const times = timesOf(beo);
        return {
          beo,
          times,
          state: statusOf(beo, clock),
          record: history.get(beo.id) ?? null,
          live: clock.minutes >= times.posted,
          /** Negative once the deadline has gone. */
          remaining: times.setupBy - clock.minutes,
        };
      });
    },

    /**
     * Takes an event on.
     *
     * Refusals are returned rather than thrown, because every one of them is
     * something the player needs told: you already have one, that one is done,
     * it is not up yet, it is already too late to start.
     */
    accept(beo, clock, { onTheClock = true } = {}) {
      if (active) return { ok: false, reason: 'already-accepted', active };
      if (history.has(beo.id)) return { ok: false, reason: 'already-done' };
      if (!onTheClock) return { ok: false, reason: 'not-on-the-clock' };

      const times = timesOf(beo);
      if (clock.minutes < times.posted) return { ok: false, reason: 'not-posted' };
      if (clock.minutes >= times.setupBy) return { ok: false, reason: 'too-late' };

      /*
       * The accepted event gets its own copy of the shortfall list.
       *
       * Everything downstream writes to it — resolved, via, delivered, and the
       * entries claims invents when a linen ends up in a corridor — and the
       * event it came off is either the day's catalogue or, for the reference
       * event, a frozen object literal in `beos.js` that is handed out by
       * reference. Without this, covering a shortfall on Tuesday leaves it
       * covered when the same card comes back, and a headless script that
       * accepts the benchmark twice is not running the benchmark the second
       * time. The generated ones get away with it only because a day is
       * re-rolled from the seed every time it is asked for.
       */
      active = {
        beo: { ...beo, shortfalls: (beo.shortfalls ?? []).map((entry) => ({ ...entry })) },
        times,
        acceptedAt: clock.minutes,
        day: clock.day,
      };
      emit('accepted', active);
      return { ok: true, active };
    },

    /** Minutes left on the accepted event, or null if there is not one. */
    remaining(clock) {
      if (!active) return null;
      return active.times.setupBy - clock.minutes;
    },

    /**
     * Watches the deadlines: yours, and everybody else's.
     *
     * Two jobs. The accepted event fires `due` once at its setup time and
     * `deadline` once at its doors, and does not close it out — grading the
     * room is not this module's business. Every *other* posted event whose
     * setup time has gone resolves without you, which is the whole reason the
     * board is allowed to post more than fits.
     */
    update(clock) {
      if (active && !active.due && clock.minutes >= active.times.setupBy) {
        active.due = true;
        emit('due', active);
      }
      if (active && !active.expired && clock.minutes >= active.times.doors) {
        active.expired = true;
        emit('deadline', active);
      }

      // A day that rolled over takes everything still standing on it with it.
      if (lastDay !== null && lastDay !== clock.day) sweep(lastDay, Infinity);
      lastDay = clock.day;
      sweep(clock.day, clock.minutes);
    },

    /** Forces a day's leftovers to resolve. Used when sleeping past them. */
    sweepDay: (day) => sweep(day, Infinity),

    /** Files the result. `grade: null` means the room was never set. */
    close({ grade, score, breakdown }, clock) {
      if (!active) return null;
      const record = {
        id: active.beo.id,
        code: active.beo.code,
        title: active.beo.title,
        day: active.day,
        acceptedAt: active.acceptedAt,
        closedAt: clock.minutes,
        onTime: clock.minutes <= active.times.setupBy,
        grade,
        score,
        breakdown,
      };
      history.set(active.beo.id, record);
      const finished = active;
      active = null;
      emit('closed', { record, beo: finished.beo });
      return record;
    },

    /** Drops the accepted event without filing anything. Used when a run resets. */
    abandon() {
      active = null;
    },

    toJSON: () => ({
      lastDay,
      history: [...history.values()],
      injected: injected.map((beo) => beo.id),
      active: active && {
        id: active.beo.id,
        acceptedAt: active.acceptedAt,
        day: active.day,
        // Shortfall state rides with the accepted event: which ones were
        // resolved, and how. Regenerating it from the seed would silently undo
        // every favour the player spent getting them covered.
        shortfalls: active.beo.shortfalls ?? [],
      },
    }),

    load(state, clock, { reference = null } = {}) {
      history = new Map((state?.history ?? []).map((record) => [record.id, record]));
      lastDay = state?.lastDay ?? null;
      injected = reference && (state?.injected ?? []).includes(reference.id) ? [reference] : [];
      active = null;
      if (!state?.active) return;
      const beo = catalogueFor(state.active.day).find((entry) => entry.id === state.active.id);
      if (!beo) return;
      if (state.active.shortfalls) beo.shortfalls = state.active.shortfalls;
      active = {
        beo,
        times: timesOf(beo),
        acceptedAt: state.active.acceptedAt,
        day: state.active.day,
      };
      // Re-arm both if they went by while the tab was closed.
      if (clock && clock.minutes >= active.times.setupBy) active.due = false;
      if (clock && clock.minutes >= active.times.doors) active.expired = false;
    },
  };
}
